import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withRouterLegacy } from 'core/routerUtils';
import { withTranslation } from 'react-i18next';
import { AlertMessage, SimpleModal, Spinner, EventDashboardDetails } from 'shared/components';
import { MaterialRanges, RoomTypeAndTiming } from 'features/material-chain';
import * as materialChainActions from 'features/material-chain/duck';
import * as roomActions from 'features/room-builder/duck';
import * as taskActions from 'features/task-management/duck';
import * as tcEventActions from 'features/tc-event-dashboard/duck';
import {
  mapRoomRanges,
  mapEventMaterials,
  mapEventMaterialTypes,
} from 'features/material-chain/selectors';
import { filter, isEmpty, isUndefined, cloneDeep, some, isEqual } from 'lodash';
import { ROOM_STATUS } from 'shared/constants';
import { ValidationService } from 'shared/services';
import {
  materialRangeRules,
  materialRangeClosedRoomRules,
  materialRangeErrorStrings,
  materialRangeClosedRoomErrorStrings,
} from 'shared/validations';
import * as MaterialValidation from '../validations';
import SeatIcon from 'assets/svgs/deskIcon.svg';

import './RoomRanges.scss';
import { addLeadingZeros, isRoomOnline } from 'shared/helpers';

const validNumberInput = (value) => {
  if (+value > 999 || value.length > 3) {
    return;
  }
  if (+value === 0 && value.length > 1) {
    return;
  }
  if (value.length > 1 && +value[0] === 0) {
    return value.substring(1);
  }

  return value;
};

class RoomRanges extends Component {
  constructor(props) {
    super(props);

    this.state = {
      ...this.resetErrors(),
      discardModalOpen: false,
      isDirty: false,
      showAlertMessage: false,
      alertMessage: '',
      room: {},
      roomMaterials: [],
      removeModalOpen: false,
      editTypeTimingOpen: false,
    };

    this.createRangeObject = this.createRangeObject.bind(this);
    this.resetErrors = this.resetErrors.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleLeadingZero = this.handleLeadingZero.bind(this);
    this.handleRemove = this.handleRemove.bind(this);
    this.handleAdd = this.handleAdd.bind(this);
    this.removeRange = this.removeRange.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.isValid = this.isValid.bind(this);
    this.closeRemoveModal = this.closeRemoveModal.bind(this);
    this.handleBack = this.handleBack.bind(this);
    this.closeDiscardModal = this.closeDiscardModal.bind(this);
    this.removeAlertMessage = this.removeAlertMessage.bind(this);
    this.toggleEditTypeTiming = this.toggleEditTypeTiming.bind(this);
  }

  componentDidMount() {
    const eventId = this.props.match.params.eventId;
    const roomId = this.props.match.params.roomId;
    const { actions, tcEvent } = this.props;

    actions.getTCEventMaterialTypes(eventId);
    actions.getEventAllMaterials(eventId);
    actions.getRoomMaterials(roomId);
    actions.getRoomStatus(roomId);
    actions.getRoom(roomId).then((room) => {
      actions.getRoomTypeMaterialTypes(room.roomType.id);
    });

    const alertMessage = sessionStorage.getItem('successRoomTypeMessage');

    if (isEmpty(tcEvent) || tcEvent.id !== +eventId) {
      actions.getTcEvent(eventId);
    }

    if (alertMessage) {
      sessionStorage.removeItem('successRoomTypeMessage');
      this.setState({
        showAlertMessage: true,
        alertMessage: alertMessage,
      });
    }
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.roomMaterials &&
      this.props.tcEventMaterialTypes &&
      this.props.roomTypeMaterialTypes &&
      (prevProps.roomMaterials !== this.props.roomMaterials ||
        prevProps.tcEventMaterialTypes !== this.props.tcEventMaterialTypes ||
        prevProps.roomTypeMaterialTypes !== this.props.roomTypeMaterialTypes)
    ) {
      let ranges = [];
      this.props.tcEventMaterialTypes.map((materialType) => {
        if (this.props.roomMaterials[materialType.id]) {
          ranges[materialType.id] = this.props.roomMaterials[materialType.id];
        } else {
          if (some(this.props.roomTypeMaterialTypes, { id: materialType.id })) {
            ranges[materialType.id] = [this.createRangeObject(materialType.id)];
          }
        }
      });
      this.setState({
        roomMaterials: ranges,
      });
    }

    if (
      !isEmpty(this.props.room) &&
      !this.props.room.loading &&
      (!isEqual(this.props.room, prevProps.room) || isEmpty(this.state.room))
    ) {
      this.setState({
        room: {
          ...this.props.room,
        },
      });
    }
  }

  componentWillUnmount() {
    this.setState({
      room: {},
    });
  }

  createRangeObject(materialTypeId) {
    const roomId = this.props.match.params.roomId;

    return {
      roomId: +roomId,
      materialTypeId: +materialTypeId,
      rangeBegin: '',
      rangeEnd: '',
      count: '',
      usedCount: null,
      unusedCount: null,
    };
  }

  resetErrors() {
    return {
      errors: [],
      errorAnswer: '',
    };
  }

  handleChange(e, materialTypeId) {
    let roomMaterials = cloneDeep(this.state.roomMaterials);
    const [field, index] = e.target.name.split('-');
    const { value } = e.target;

    if (!materialTypeId && field === 'room') {
      const validNumber = validNumberInput(value);
      if (!validNumber && validNumber !== '') {
        return;
      }

      this.setState((prevState) => ({
        room: {
          ...prevState.room,
          [index]: validNumber,
        },
        errors: {
          ...prevState.errors,
          [index]: '',
        },
        isDirty: true,
      }));
    }

    if (materialTypeId && field && !isNaN(index)) {
      roomMaterials[materialTypeId][index][field] = (field === 'count' ? +value : value) || '';

      this.setState({
        ...this.resetErrors(),
        roomMaterials: roomMaterials,
        isDirty: true,
      });
    }
  }

  handleLeadingZero(e, materialTypeId) {
    let roomMaterials = cloneDeep(this.state.roomMaterials);
    const [field, index] = e.target.name.split('-');
    const { value } = e.target;

    if (materialTypeId && field && !isNaN(index)) {
      roomMaterials[materialTypeId][index][field] = addLeadingZeros(value);

      this.setState({
        ...this.resetErrors(),
        roomMaterials: roomMaterials,
        isDirty: true,
      });
    }
  }

  handleRemove(materialTypeId, index) {
    const { rangeBegin, rangeEnd, count } = this.state.roomMaterials[materialTypeId][index];

    if (rangeBegin || rangeEnd || count) {
      this.setState({
        removeModalOpen: true,
        removeMaterialTypeId: materialTypeId,
        removeRangeIndex: index,
        isDirty: true,
      });
    } else {
      this.removeRange(materialTypeId, index);
    }
  }

  removeRange(materialTypeId, index) {
    const { removeMaterialTypeId, removeRangeIndex } = this.state;

    if (isUndefined(materialTypeId) && isUndefined(index)) {
      materialTypeId = removeMaterialTypeId;
      index = removeRangeIndex;
    }

    let roomMaterials = cloneDeep(this.state.roomMaterials);
    roomMaterials[materialTypeId].splice(index, 1);

    this.setState({
      ...this.resetErrors(),
      roomMaterials: roomMaterials,
      removeModalOpen: false,
      removeMaterialTypeId: null,
      removeRangeIndex: null,
      isDirty: true,
    });
  }

  handleAdd(materialTypeId) {
    let roomMaterials = cloneDeep(this.state.roomMaterials);
    roomMaterials[materialTypeId].push(this.createRangeObject(materialTypeId));

    this.setState({
      ...this.resetErrors(),
      roomMaterials: roomMaterials,
      isDirty: true,
    });
  }

  async handleSave() {
    const roomId = this.props.match.params.roomId;
    const { actions } = this.props;
    const roomMaterials = cloneDeep(this.state.roomMaterials);
    let data = roomMaterials.flat().filter((n) => n);

    if (this.isValid()) {
      const response = await actions.setRoomMaterials(roomId, {
        roomMaterials: data,
      });

      if (response.data && response.data.message) {
        this.setState({
          showAlertMessage: true,
          alertMessage: response.data.message,
          alertIsError: true,
        });
      } else {
        sessionStorage.setItem(
          'successSavedMessage',
          'testCenter.testMaterialsRanges.successSavedMessage'
        );
        this.props.history.goBack();
      }
    }
  }

  isValid() {
    const { room, roomMaterials } = this.state;
    const { allEventRanges, allRoomsRanges, tcEvent } = this.props;

    let errors = {};
    let rangeErrors = [];
    const filteredRoomMaterials = roomMaterials.filter((n) => n);

    if (filteredRoomMaterials.length) {
      roomMaterials.map((ranges, materialTypeId) => {
        ranges &&
          ranges.map((range, index) => {
            let error =
              room.status === ROOM_STATUS.closed
                ? ValidationService.validate(
                    range,
                    materialRangeClosedRoomRules,
                    materialRangeClosedRoomErrorStrings
                  )
                : ValidationService.validate(range, materialRangeRules, materialRangeErrorStrings);

            if (!isEmpty(error)) {
              rangeErrors.push({
                ...error,
                materialTypeId,
                index,
                fieldsMandatory: true,
              });
            }
          });
      });

      rangeErrors = rangeErrors.concat(
        tcEvent.equatingFlag ? [] : MaterialValidation.checkRangeInterval(roomMaterials),
        tcEvent.equatingFlag ? [] : MaterialValidation.checkRangeOverlap(roomMaterials),
        tcEvent.equatingFlag
          ? []
          : MaterialValidation.checkRoomRangeOverlap(roomMaterials, allRoomsRanges),
        tcEvent.equatingFlag
          ? []
          : MaterialValidation.checkRangeEvent(roomMaterials, allEventRanges),
        tcEvent.equatingFlag ? [] : MaterialValidation.checkTotalCount(roomMaterials)
      );

      if (!isEmpty(rangeErrors)) {
        errors['materials'] = rangeErrors;
      }
    }

    if (room.status === ROOM_STATUS.closed) {
      errors = Object.assign({}, errors);

      if (!isEmpty(rangeErrors)) {
        errors['materials'] = rangeErrors;
      }
      // if (MaterialValidation.checkUsedMaterials(roomMaterials)) {
      //   errors['totalBooklets'] = 'testCenter.testMaterialsRanges.error.totalBooklets';
      // }
    }

    this.setState({
      errors,
    });

    return !Object.keys(errors).length;
  }

  toggleEditTypeTiming(showType) {
    const { actions } = this.props;
    const { room } = this.state;
    const isTimingModal = !this.state.showType;
    document.activeElement.focus = false;

    this.setState(
      (prevState) => ({
        editTypeTimingOpen: !prevState.editTypeTimingOpen,
        showType,
      }),
      () => {
        if (showType && !this.state.editTypeTimingOpen) {
          actions.getRoomTypeMaterialTypes(room.roomType.id);
          this.setState({
            showAlertMessage: true,
            alertMessage: isTimingModal
              ? 'testCenter.testMaterialsRanges.successRoomTimingMessage'
              : 'testCenter.testMaterialsRanges.successRoomTypeMessage',
            alertIsError: false,
          });
        }
      }
    );
  }

  closeRemoveModal() {
    this.setState({
      removeModalOpen: false,
    });
  }

  handleBack() {
    if (this.state.isDirty) {
      this.setState({
        discardModalOpen: true,
      });
    } else {
      this.props.history.goBack();
    }
  }

  closeDiscardModal(goBack) {
    if (goBack) {
      this.props.history.goBack();
    }
    this.setState({
      discardModalOpen: false,
    });
  }

  removeAlertMessage() {
    this.setState({
      showAlertMessage: false,
      alertMessage: '',
    });
  }

  render() {
    const {
      loading,
      eventMaterials,
      roomStatus,
      roomTypeMaterialTypes,
      tcEventMaterialTypes,
      tcEvent,
    } = this.props;

    const {
      alertMessage,
      alertIsError,
      discardModalOpen,
      errors,
      showAlertMessage,
      room,
      roomMaterials,
      removeModalOpen,
      editTypeTimingOpen,
      showType,
    } = this.state;

    const locationData = {
      name: roomStatus.locationName,
      area: roomStatus.locationArea,
    };

    if (loading || isEmpty(room) || room.loading || isEmpty(tcEvent)) {
      return <Spinner />;
    }

    return (
      <div className="room-ranges">
        <div className="mb-30 title-container">
          <h4 className="pt-3">
            {this.props.t(
              isRoomOnline(room.roomType)
                ? 'testCenter.testMaterialsRanges.titleOnline'
                : 'testCenter.testMaterialsRanges.title'
            )}{' '}
          </h4>
          <EventDashboardDetails date={tcEvent.testingEvent.name} name={roomStatus.testCenterName}>
            {locationData.name}
            {`${locationData.area ? ', ' + locationData.area : ''}`}
          </EventDashboardDetails>
          <div className="room-details">
            <span>{room.name}</span>
            <div>
              <SeatIcon />
              {room.capacity} {this.props.t('common.labels.seats')}
            </div>
          </div>
        </div>

        <div className="room-type-timing-section">
          <div className="">
            {(room.roomType && room.roomType.name) ||
              this.props.t('testCenter.roomDashboard.noTypeLabel')}
            {room.status === ROOM_STATUS.inactive && (
              <button className="btn-link fw-bold" onClick={() => this.toggleEditTypeTiming(true)}>
                {this.props.t('common.labels.edit')}
              </button>
            )}
          </div>
          {room.timing && (
            <div className="">
              {this.props.mappedRoomTiming[room.timing]?.name}
              {room.status === ROOM_STATUS.inactive && (
                <button
                  className="btn-link fw-bold"
                  onClick={() => this.toggleEditTypeTiming(false)}>
                  {this.props.t('common.labels.edit')}
                </button>
              )}
            </div>
          )}
          <div>
            {!room.roomType
              ? ''
              : room.roomType.onlineFlag
                ? this.props.t('common.labels.online')
                : this.props.t('common.labels.paper')}
          </div>
        </div>

        {/*{errors.totalBooklets && <AlertNotice message={this.props.t(errors.totalBooklets)} type="error" /> }*/}

        <div className="room-ranges-container">
          {!isRoomOnline(room.roomType) &&
            tcEventMaterialTypes?.map((materialType) =>
              roomMaterials[materialType.id] &&
              (roomMaterials[materialType.id].length ||
                (roomTypeMaterialTypes.length &&
                  !(
                    !roomMaterials[materialType.id].length &&
                    !some(roomTypeMaterialTypes, { id: materialType.id })
                  ))) ? (
                <MaterialRanges
                  materialType={materialType}
                  materialsRanges={roomMaterials[materialType.id]}
                  key={`material-${materialType.id}`}
                  handleRemove={this.handleRemove}
                  handleAdd={this.handleAdd}
                  handleChange={this.handleChange}
                  handleLeadingZero={this.handleLeadingZero}
                  parentRanges={eventMaterials[materialType.id]}
                  roomClosed={room.status === ROOM_STATUS.closed}
                  materialTypeNotMatch={
                    !!roomTypeMaterialTypes.length &&
                    !some(roomTypeMaterialTypes, { id: materialType.id })
                  }
                  showRemoveBtn
                  errors={
                    errors.materials &&
                    filter(errors.materials, {
                      materialTypeId: materialType.id,
                    })
                  }
                />
              ) : null
            )}
        </div>

        <div className="buttons-container w-100">
          <button className="btn btn-warn w-50" onClick={this.handleBack}>
            {this.props.t('common.labels.back')}
          </button>
          <button className="btn btn-action w-50" onClick={this.handleSave}>
            {' '}
            {this.props.t('common.labels.save')}
          </button>
        </div>

        <RoomTypeAndTiming
          room={room}
          location={locationData}
          modalOpen={editTypeTimingOpen}
          closeModal={this.toggleEditTypeTiming}
          editType={showType}
          cycleId={tcEvent.testingEvent.adminId.cycleId.id}
          id={this.props.match.params.eventId}
        />

        <SimpleModal
          isOpen={removeModalOpen}
          onCancel={this.closeRemoveModal}
          onConfirm={() => this.removeRange()}
          confirmLabel={this.props.t('testCenter.testMaterialsRanges.removeModalConfirmBtn')}
          cancelLabel={this.props.t('common.labels.back')}
          headline={this.props.t('testCenter.testMaterialsRanges.removeModalTitle')}
          message={this.props.t('testCenter.testMaterialsRanges.removeRoomRangeModalMessage')}
        />

        <SimpleModal
          isOpen={discardModalOpen}
          onCancel={() => this.closeDiscardModal(false)}
          onConfirm={() => this.closeDiscardModal(true)}
          confirmLabel={this.props.t('common.labels.yes')}
          cancelLabel={this.props.t('common.labels.no')}
          headline={this.props.t('common.backModal.title')}
          message={this.props.t('common.backModal.discardPageMessage')}
          buttonsReverseOrder
        />

        <AlertMessage
          show={showAlertMessage}
          removeMessage={this.removeAlertMessage}
          message={alertMessage}
          isError={alertIsError}
        />
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    roomStatus: state.taskManagement.roomStatus,
    room: state.roomOptions.room || {},
    loading: state.materialChain.loading,
    tcEventMaterialTypes: mapEventMaterialTypes(state),
    roomTypeMaterialTypes: state.materialChain.roomTypeMaterialTypes,
    roomMaterials: mapRoomRanges(state),
    eventMaterials: mapEventMaterials(state),
    allEventRanges: state.materialChain.eventAllMaterials.testCenterEventMaterials || [],
    allRoomsRanges: state.materialChain.eventAllMaterials.roomMaterials || [],
    tcEvent: state.tcEvent.currentEvent || {},
    mappedRoomTiming: state.statics.mappedRoomTiming,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        ...materialChainActions,
        ...roomActions,
        ...taskActions,
        ...tcEventActions,
      },
      dispatch
    ),
  };
}

export default withTranslation()(
  withRouterLegacy(connect(mapStateToProps, mapDispatchToProps)(RoomRanges))
);

RoomRanges.propTypes = {
  actions: PropTypes.shape({
    getTCEventMaterialTypes: PropTypes.func.isRequired,
    getEventAllMaterials: PropTypes.func.isRequired,
    getRoomMaterials: PropTypes.func.isRequired,
    getRoomStatus: PropTypes.func.isRequired,
    getRoom: PropTypes.func.isRequired,
    getRoomTypeMaterialTypes: PropTypes.func.isRequired,
    getTcEvent: PropTypes.func.isRequired,
    setRoomMaterials: PropTypes.func.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      eventId: PropTypes.string,
      roomId: PropTypes.string,
    }).isRequired,
  }).isRequired,
  history: PropTypes.shape({
    goBack: PropTypes.func.isRequired,
  }).isRequired,
  tcEvent: PropTypes.shape({
    id: PropTypes.number,
    loading: PropTypes.bool,
    equatingFlag: PropTypes.bool,
    testingEvent: PropTypes.shape({
      name: PropTypes.string,
      adminId: PropTypes.shape({
        cycleId: PropTypes.shape({
          id: PropTypes.number,
        }),
      }),
    }),
  }),
  room: PropTypes.shape({
    loading: PropTypes.bool,
    name: PropTypes.string,
    status: PropTypes.string,
    roomType: PropTypes.shape({
      id: PropTypes.number,
    }),
  }),
  roomStatus: PropTypes.shape({
    eventDate: PropTypes.string,
    status: PropTypes.string,
  }),
  loading: PropTypes.bool,
  tcEventMaterialTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
    })
  ),
  roomTypeMaterialTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
    })
  ),
  roomMaterials: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape({}))),
  eventMaterials: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape({}))),
  allEventRanges: PropTypes.arrayOf(PropTypes.shape({})),
  allRoomsRanges: PropTypes.arrayOf(PropTypes.shape({})),
  mappedRoomTiming: PropTypes.arrayOf(PropTypes.shape({})),
  t: PropTypes.func.isRequired,
};
